/**
 * Cash rounding rule at this till (ops-simulation T07).
 *
 * The rule — a step in minor units and a mode — is the active location's
 * override, else the tenant setting, else the currency default. It comes from
 * GET /auth/me (`tenant.cash_rounding_*` and each `locations[].cash_rounding_*`),
 * which the auth store keeps in localStorage, so an offline till rounds by the
 * same rule. The API recomputes it with the same helper (`@bbpos/utils`).
 *
 * Held module-level, like the tenant currency in the cart store: the cart
 * selectors and the offline service read it outside React.
 */
import {
  getSubunitMultiplier,
  resolveCashRoundingRule,
  type CashRoundingRule,
  type StoredCashRounding,
} from '@bbpos/utils';
import { useLocationStore } from '@/stores/use-location-store';

interface RoundingProfile {
  tenant?: { cash_rounding_step?: number | null; cash_rounding_mode?: string | null } | null;
  locations?: Array<{ id: string; cash_rounding_step?: number | null; cash_rounding_mode?: string | null }>;
}

/** undefined = the profile predates T07 (no field at all) → keep the old whole-unit rule. */
let tenantRule: StoredCashRounding | undefined;
let locationRules: Record<string, StoredCashRounding> = {};

export function setCashRoundingSettings(profile: RoundingProfile | null | undefined): void {
  const t = profile?.tenant;
  tenantRule = t && 'cash_rounding_step' in t
    ? { step: t.cash_rounding_step ?? null, mode: t.cash_rounding_mode ?? null }
    : undefined;
  locationRules = {};
  for (const l of profile?.locations ?? []) {
    if (l.cash_rounding_step != null) {
      locationRules[l.id] = { step: l.cash_rounding_step, mode: l.cash_rounding_mode ?? null };
    }
  }
}

/** The rule for `locationId` (default: the till's active location). */
export function cashRoundingRuleFor(
  currency: string,
  locationId: string | null | undefined = useLocationStore.getState().currentLocationId,
): CashRoundingRule {
  const location = locationId ? locationRules[locationId] : undefined;
  if (tenantRule === undefined && !location) {
    return { step: getSubunitMultiplier(currency), mode: 'HALF_UP' };
  }
  return resolveCashRoundingRule(currency, tenantRule ?? null, location ?? null);
}
